import React, { useEffect, useRef, useState } from 'react';
import { PencilIcon, PlusIcon } from 'lucide-react';
import { STATUS_NOTE_MAX } from '../../utils/registrations';

/*
 * Status note in the table (from the reference's StatusNoteCell): the note, or "Add note"; click to
 * edit. Enter or clicking away saves, Escape cancels. `onSave(text)` resolves true once the API has
 * saved it; until then the box shows "Saving…" and can't be changed. On failure the box stays open
 * with the text, so it can be saved again or cancelled (the page shows the error).
 *
 * A button and an input, so a click here never opens the drawer (RegistrationsTable skips them).
 */
export function StatusNoteCell({ value, camperName, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  const cancelled = useRef(false);
  // Also set while saving: disabling the focused box can fire blur, which must not save twice.
  const savingRef = useRef(false);
  const mounted = useRef(true);

  // Set on every mount: StrictMode (dev) mounts, unmounts and mounts again, so a ref only cleared on
  // unmount would read "gone" for good and leave the box on "Saving…".
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (editing && !saving) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing, saving]);

  const start = () => {
    cancelled.current = false;
    setDraft(value);
    setEditing(true);
  };

  const commit = async () => {
    if (cancelled.current || savingRef.current) return;
    const next = draft.trim();
    if (next === value) {
      setEditing(false);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const ok = await onSave(next);
    savingRef.current = false;
    if (!mounted.current) return;
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="tw-relative -tw-ml-2 tw-w-[calc(100%+0.5rem)]">
        <input
          ref={inputRef}
          value={draft}
          maxLength={STATUS_NOTE_MAX}
          disabled={saving}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape' && !saving) {
              cancelled.current = true;
              setEditing(false);
            }
          }}
          aria-label={`Status note for ${camperName}`}
          className="tw-h-8 tw-w-full tw-rounded-md tw-border tw-border-brand-500 tw-bg-white tw-pl-2 tw-pr-16 tw-text-sm tw-text-neutral-900 tw-outline-none tw-ring-2 tw-ring-brand-500/20 disabled:tw-bg-sand-50 disabled:tw-text-neutral-500"
        />
        <span className="tw-pointer-events-none tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-[11px] tw-tabular-nums tw-text-neutral-400">
          {saving ? 'Saving…' : `${draft.length}/${STATUS_NOTE_MAX}`}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      aria-label={value ? `Edit status note for ${camperName}: ${value}` : `Add status note for ${camperName}`}
      className="tw-group/note -tw-ml-2 tw-flex tw-h-8 tw-w-[calc(100%+0.5rem)] tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2 tw-text-left tw-text-sm tw-transition-colors tw-duration-100 hover:tw-bg-sand-50">
      {value ? (
        <span className="tw-truncate tw-text-neutral-900" title={value}>{value}</span>
      ) : (
        <span className="tw-flex tw-items-center tw-gap-1 tw-text-neutral-400">
          <PlusIcon className="tw-h-3.5 tw-w-3.5" aria-hidden />
          Add note
        </span>
      )}
      <PencilIcon className="tw-ml-auto tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-neutral-400 tw-opacity-0 tw-transition-opacity tw-duration-150 group-hover/note:tw-opacity-100" aria-hidden />
    </button>
  );
}
